import React, { useMemo, useEffect, useState } from 'react';
import { Receipt, X, Gift, Tag } from 'lucide-react';
import { useCurrencyState } from '../../stores/subscriptionSelectors';
import { useTranslation } from 'react-i18next';
import configService from '../../services/configService';
import { formatPrice } from '../../utils/priceFormat';

interface PromotionDetails {
  isLoaded: boolean;
  isValid: boolean;
  percentOff: number;
  amountOff: number;
  couponId: string;
  description: string;
  allowedPaymentTypes?: ('one_time' | 'subscription')[];
  duration?: 'once' | 'forever' | 'repeating';
  durationInMonths?: number | null;
  minimumAmount?: number;
  currency?: string | null;
  errorMessage?: string;
  error?: string;
}

export interface OrderSummarySectionProps {
  subscriptionLevel: 'standard' | 'premium';
  subscriptionType: 'monthly' | 'yearly' | 'trial';
  paymentType: 'one_time' | 'subscription';

  // Prices for the selected subscriptionLevel
  currentBasePriceForLevel: number; 
  currentBasePriceForLevel_usd?: number;

  originalRegularPriceForLevel: number;
  originalRegularPriceForLevel_usd?: number;

  promotionCode?: string | null;
  promotionDetails?: PromotionDetails;
  onClearPromoCode?: () => void;
  selectedPaymentMethod?: string;
  onTotalCalculated?: (total: number) => void;
  serviceType?: 'flower' | 'fruit' | 'cat_grass';
  showNewUserGift?: boolean;
  vaseImage?: string;
  scissorsImage?: string;
}

const OrderSummarySection: React.FC<OrderSummarySectionProps> = ({
  subscriptionLevel,
  subscriptionType,
  paymentType,
  currentBasePriceForLevel,
  currentBasePriceForLevel_usd,
  originalRegularPriceForLevel,
  originalRegularPriceForLevel_usd,
  promotionCode,
  promotionDetails,
  onClearPromoCode,
  selectedPaymentMethod,
  onTotalCalculated,
  serviceType = 'flower',
  showNewUserGift = false,
  vaseImage,
  scissorsImage
}) => {
  const { currency, availableCurrencies } = useCurrencyState();
  const { t } = useTranslation();
  
  // Get currency symbol
  const currencySymbol = availableCurrencies.find(c => c.code === currency)?.symbol || '฿';
  
  // State to store crypto pricing from config service
  const [cryptoPricing, setCryptoPricing] = useState<any>(null);
  
  // Fetch crypto pricing configuration from config service
  useEffect(() => {
    const fetchCryptoPricing = async () => {
      try {
        const cryptoPricingData = await configService.getCryptoPricing(serviceType);
        setCryptoPricing(cryptoPricingData);
      } catch (error) {
        console.error('Error fetching crypto pricing:', error);
        // Keep default null value so hardcoded fallbacks are used
      }
    };

    fetchCryptoPricing();
  }, [serviceType]);
  
  // Helper function to get crypto pricing from config service or fallback to hardcoded
  const getCryptoPriceFromConfig = (subscriptionLevel: string) => {
    if (cryptoPricing) {
      const levelPricing = cryptoPricing[subscriptionLevel as 'standard' | 'premium'];
      return levelPricing?.usd || 
        (serviceType === 'fruit'
          ? (subscriptionLevel === 'premium' ? 39.99 : 27.99)
          : serviceType === 'cat_grass'
          ? (subscriptionLevel === 'premium' ? 11.99 : 7.99)
          : (subscriptionLevel === 'premium' ? 15.99 : 9.99));
    } else {
      // Fallback to hardcoded values
      return serviceType === 'fruit'
        ? (subscriptionLevel === 'premium' ? 39.99 : 27.99)
        : serviceType === 'cat_grass'
        ? (subscriptionLevel === 'premium' ? 11.99 : 7.99)
        : (subscriptionLevel === 'premium' ? 15.99 : 9.99);
    }
  };
  
  // Memoize all price calculations
  const {
    basePrice,
    adjustedBasePrice,
    originalPrice,
    subscriptionDiscount,
    priceBeforePromotion,
    originalRegularPrice,
    isPromotionCompatible,
    meetsMinimumAmount,
    promotionCurrencyMatches,
    promotionDiscount,
    hasCryptoSpecialPricing,
    total,
    shouldApplyDiscount
  } = useMemo(() => {
    // Determine if using USD prices
    const useUSD = currency === 'usd';
    
    // Select the appropriate base price based on currency
    const basePrice = useUSD && currentBasePriceForLevel_usd ? currentBasePriceForLevel_usd : currentBasePriceForLevel;
    
    // For trial subscriptions, no billing period discounts - use price as-is
    let adjustedBasePrice: number;
    let subscriptionDiscount: number;
    
    if (subscriptionType === 'trial') {
      adjustedBasePrice = basePrice;
      subscriptionDiscount = 0;
    } else if (subscriptionType === 'yearly') {
      const yearlyPrice = basePrice * 12;
      if (paymentType === 'subscription') {
        // Combined yearly + subscription discount: 15% total discount
        adjustedBasePrice = yearlyPrice * 0.85;
        subscriptionDiscount = yearlyPrice * 0.15; // Total discount amount
      } else {
        // Only yearly discount: 10% discount
        adjustedBasePrice = yearlyPrice * 0.9;
        subscriptionDiscount = 0;
      }
    } else {
      // Monthly subscription
      if (paymentType === 'subscription') {
        adjustedBasePrice = basePrice * 0.95; // 5% discount
        subscriptionDiscount = basePrice * 0.05;
      } else {
        adjustedBasePrice = basePrice;
        subscriptionDiscount = 0;
      }
    }
    
    // Original price before any discounts
    const originalPrice = subscriptionType === 'yearly' ? basePrice * 12 : basePrice;
    
    // Price after all discounts except promotion code
    const priceBeforePromotion = adjustedBasePrice;
    
    // Select the appropriate original price based on currency and subscription level, using API values
    // Ensure a fallback to 0 if API props (even those typed as 'number') are unexpectedly undefined at runtime.
    let resolvedOriginalRegularPriceForLevel: number;
    if (useUSD && originalRegularPriceForLevel_usd !== undefined) {
      resolvedOriginalRegularPriceForLevel = originalRegularPriceForLevel_usd;
    } else {
      resolvedOriginalRegularPriceForLevel = originalRegularPriceForLevel ?? 0;
    }
    const originalRegularPrice = resolvedOriginalRegularPriceForLevel;
    
    // Check if promotion is compatible with selected payment type
    const isPromotionCompatible = promotionDetails?.isValid && promotionDetails?.isLoaded && 
      (!promotionDetails.allowedPaymentTypes || 
       promotionDetails.allowedPaymentTypes.length === 0 || 
       promotionDetails.allowedPaymentTypes.includes(paymentType));
       
    // Check if promotion meets minimum amount requirement - use price after other discounts
    const EPSILON = 0.001; // Small tolerance for floating point comparison
    const meetsMinimumAmount = !promotionDetails?.minimumAmount || 
      priceBeforePromotion + EPSILON >= (promotionDetails.minimumAmount || 0);
    
    // Check if promotion currency matches the selected currency
    const promotionCurrencyMatches = !promotionDetails?.currency || 
      promotionDetails.currency === currency;

    // Calculate promotion discount only if compatible, meets minimum amount, and currency matches
    // Also exclude trial subscriptions from promotion codes
    const rawPromotionDiscount = (subscriptionType !== 'trial' && isPromotionCompatible && meetsMinimumAmount && promotionCurrencyMatches)
      ? (promotionDetails.percentOff > 0 
          ? (priceBeforePromotion) * (promotionDetails.percentOff / 100) 
          : (promotionDetails.amountOff || 0))
      : 0;
      
    // Limit the promotion discount to ensure total doesn't go below zero
    const promotionDiscount = Math.min(rawPromotionDiscount, priceBeforePromotion);
    
    // Check if crypto special pricing applies (monthly subscription with USD and crypto payment method)
    const hasCryptoSpecialPricing = selectedPaymentMethod === 'crypto' && currency === 'usd' && subscriptionType === 'monthly';
    
    // Calculate the final total based on all discounts
    let total = Math.max(0, priceBeforePromotion - promotionDiscount);
    
    // If crypto special pricing applies, use fixed prices
    if (hasCryptoSpecialPricing) {
      total = getCryptoPriceFromConfig(subscriptionLevel);
    }

    // Determine if we should apply the discount based on CURRENT values, ignoring any stale errorMessage
    // Also exclude trial subscriptions from promotion codes
    const shouldApplyDiscount = subscriptionType !== 'trial' && isPromotionCompatible && 
      meetsMinimumAmount && 
      promotionCurrencyMatches;

    return {
      basePrice,
      adjustedBasePrice,
      originalPrice,
      subscriptionDiscount,
      priceBeforePromotion,
      originalRegularPrice,
      isPromotionCompatible,
      meetsMinimumAmount,
      promotionCurrencyMatches,
      promotionDiscount,
      hasCryptoSpecialPricing,
      total,
      shouldApplyDiscount
    };
  }, [
    currency,
    currentBasePriceForLevel,
    currentBasePriceForLevel_usd,
    originalRegularPriceForLevel,
    originalRegularPriceForLevel_usd,
    subscriptionLevel,
    subscriptionType,
    paymentType,
    promotionDetails,
    selectedPaymentMethod,
    serviceType,
    getCryptoPriceFromConfig
  ]);

  // Move debug logging to useEffect
  useEffect(() => {
    if (promotionCode) {
      console.log('Order Summary Calculation:', {
        isPromotionCompatible,
        meetsMinimumAmount,
        promotionCurrencyMatches,
        priceBeforePromotion,
        minimumAmount: promotionDetails?.minimumAmount,
        promotionDiscount,
        errorMessage: promotionDetails?.errorMessage,
        hasCryptoSpecialPricing,
        total
      });
    }
  }, [
    promotionCode,
    isPromotionCompatible,
    meetsMinimumAmount,
    promotionCurrencyMatches,
    priceBeforePromotion,
    promotionDetails?.minimumAmount,
    promotionDiscount,
    promotionDetails?.errorMessage,
    hasCryptoSpecialPricing,
    total
  ]);

  // Call onTotalCalculated whenever the total changes
  useEffect(() => {
    if (onTotalCalculated) {
      onTotalCalculated(total);
    }
  }, [total, onTotalCalculated]);

  return (
    <div>
      <div className="text-sm space-y-2">
        {/* Trial subscription banner - special message for trial subscriptions */}
        {subscriptionType === 'trial' ? (
          <div className="flex items-center bg-green-50 p-2 rounded-lg mb-3">
            <Gift size={16} className="text-green-600 mr-2 flex-shrink-0" />
            <span className="text-green-700 text-xs font-medium">
              {t('subscription.plansSection.trialSpecialOffer', 'Trial Subscription - Try Our Service')}
            </span>
          </div>
        ) : (
          /* Launch special offer banner - for all currencies */
          <div className="flex items-center bg-blue-50 p-2 rounded-lg mb-3">
            <Tag size={16} className="text-blue-600 mr-2 flex-shrink-0" />
            <span className="text-blue-700 text-xs font-medium">
            {t('subscription.plansSection.launchSpecialOffer')}
            </span>
          </div>
        )}
        
        {/* For trial subscriptions, simplify the pricing display */}
        {subscriptionType === 'trial' ? (
          <div className="flex justify-between">
            <span className="text-gray-600">
              {t('subscription.orderSummary.trialPrice', 'Trial price')} ({subscriptionLevel})
            </span>
            <span className="font-medium text-green-600">
              {currencySymbol}{formatPrice(basePrice, currency)}
            </span>
          </div>
        ) : (
          <>
            {/* Original price with strikethrough - for all currencies */}
            <div className="flex justify-between mb-1">
              <span className="text-gray-500">{t('subscription.orderSummary.regularPrice')} ({subscriptionLevel})</span>
              <span className="font-medium line-through text-gray-500">
                {currencySymbol}{subscriptionType === 'yearly' ? formatPrice((originalRegularPrice || 0) * 12, currency) : formatPrice(originalRegularPrice || 0, currency)}
              </span>
            </div>
            
            {/* Base price */}
            <div className="flex justify-between">
              <span className="text-gray-600">
                {t('subscription.orderSummary.specialPrice')} ({subscriptionLevel}){subscriptionType === 'yearly' ? ' x 12' : ''}
              </span>
              <span className="font-medium text-green-600">
                {currencySymbol}{subscriptionType === 'yearly' ? formatPrice(basePrice * 12, currency) : formatPrice(basePrice, currency)}
              </span>
            </div>
            
            {/* Show combined discount for yearly + subscription, or individual discounts */}
            {subscriptionType === 'yearly' && paymentType === 'subscription' ? (
              <div className="flex justify-between text-green-600">
                <span>{t('subscription.orderSummary.yearlySubscriptionDiscount', 'Yearly + Subscription discount (15%)')}</span>
                <span>-{currencySymbol}{formatPrice(subscriptionDiscount, currency)}</span>
              </div>
            ) : (
              <>
                {/* Yearly discount if applicable */}
                {subscriptionType === 'yearly' && (
                  <div className="flex justify-between text-green-600">
                    <span>{t('subscription.orderSummary.yearlyDiscount')}</span>
                    <span>-{currencySymbol}{formatPrice(basePrice * 12 * 0.1, currency)}</span>
                  </div>
                )}
                
                {/* Subscription payment discount if applicable */}
                {paymentType === 'subscription' && subscriptionType !== 'yearly' && (
                  <div className="flex justify-between text-green-600">
                    <span>{t('subscription.orderSummary.subscriptionDiscount')}</span>
                    <span>-{currencySymbol}{formatPrice(subscriptionDiscount, currency)}</span>
                  </div>
                )}
              </>
            )}
          </>
        )}
        
        {/* Promotion code discount if valid - Hide for trial subscriptions */}
        {subscriptionType !== 'trial' && promotionDetails?.isValid && promotionDetails?.isLoaded && (
          <div className={`flex justify-between ${shouldApplyDiscount ? 'text-green-600' : 'text-gray-400'}`}>
            <span className="flex items-center">
              {promotionCode} ({promotionDetails.description})
              {!isPromotionCompatible && (
                <span className="text-xs ml-1 italic"> 
                  {promotionDetails.duration === 'repeating' 
                    ? '(subscription only)' 
                    : '(not compatible)'}
                </span>
              )}
              {promotionDetails.duration === 'repeating' && promotionDetails.durationInMonths && (
                <span className="text-xs ml-1 italic">
                  (first {promotionDetails.durationInMonths} {promotionDetails.durationInMonths === 1 ? 'month' : 'months'})
                </span>
              )}
            </span>
            <div className="flex items-center">
              {shouldApplyDiscount ? (
                <span>-{currencySymbol}{formatPrice(promotionDiscount, currency)}</span>
              ) : (
                <span>{currencySymbol}{formatPrice(0, currency)}</span>
              )}
              {onClearPromoCode && (
                <button 
                  onClick={onClearPromoCode} 
                  className="text-gray-500 hover:text-red-500 p-1 ml-2 rounded-full hover:bg-red-50 transition-colors"
                  title="Clear promotion code"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Display invalid promotion code message - Hide for trial subscriptions */}
        {subscriptionType !== 'trial' && promotionCode && !promotionDetails?.isValid && promotionDetails?.isLoaded && (
          <div className="flex justify-between text-red-500 items-center">
            <div className="flex-grow flex items-center">
              <span>
                Promotion code: {promotionCode}
              </span>
            </div>
            <div className="flex items-center">
              <span className="mr-2">Invalid</span>
              {onClearPromoCode && (
                <button 
                  onClick={onClearPromoCode} 
                  className="text-gray-500 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
                  title="Clear promotion code"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Trial subscription promotion code warning */}
        {subscriptionType === 'trial' && promotionCode && (
          <div className="flex justify-between text-amber-600 items-center bg-amber-50 p-2 rounded">
            <div className="flex-grow flex items-center">
              <span className="text-sm">
                {t('subscription.orderSummary.trialPromotionWarning', 'Promotion codes cannot be used for trial subscriptions')}
              </span>
            </div>
            <div className="flex items-center">
              {onClearPromoCode && (
                <button 
                  onClick={onClearPromoCode} 
                  className="text-gray-500 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
                  title="Clear promotion code"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Error messages for promotion code - Hide for trial subscriptions */}
        {subscriptionType !== 'trial' && promotionCode && promotionDetails?.isValid && (
          <>
            {/* Currency mismatch message takes priority */}
            {!promotionCurrencyMatches && promotionDetails?.currency && (
              <div className="flex justify-between text-amber-600 text-xs mt-1 bg-amber-50 p-2 rounded">
                <span>
                  This promotion code is only valid for {promotionDetails.currency} currency.
                </span>
              </div>
            )}
            
            {/* Minimum amount message only if currencies match */}
            {promotionCurrencyMatches && !meetsMinimumAmount && promotionDetails?.minimumAmount && (
              <div className="flex justify-between text-amber-600 text-xs mt-1 bg-amber-50 p-2 rounded">
                              <span>
                Your current purchase ({currencySymbol}{formatPrice(priceBeforePromotion, currency)}) doesn't meet the minimum amount 
                required ({currencySymbol}{formatPrice(promotionDetails.minimumAmount, currency)}) for this promotion code.
              </span>
              </div>
            )}
          </>
        )}
        
        {/* Special Crypto Pricing in Order Summary - moved here before the Total */}
        {hasCryptoSpecialPricing && (
          <div className="border-t border-gray-100 pt-3">
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center">
                <Gift size={16} className="text-green-500 mr-2" />
                <span className="text-gray-700 font-medium">Crypto Special Discount</span>
              </div>
              <span className="text-green-600 font-medium">
                Fixed price: ${formatPrice(getCryptoPriceFromConfig(subscriptionLevel), 'usd')}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Special promotional pricing when paying with cryptocurrency
            </p>
          </div>
        )}
        
        {/* Subtotal */}
        <div className="pt-1 border-t border-gray-200 flex justify-between font-medium">
          <span>{t('subscription.orderSummary.total')}</span>
          <span className="text-lg">{currencySymbol}{formatPrice(total, currency)}</span>
        </div>
        
        {/* Combined Gifts Section - Show all applicable gifts in one section */}
        {(() => {
          const hasScissorsGift = showNewUserGift && serviceType === 'flower' && subscriptionType !== 'trial';
          const hasVaseGift = subscriptionType === 'yearly' && serviceType === 'flower' && vaseImage;
          
          if (!hasScissorsGift && !hasVaseGift) return null;
          
          return (
            <div className="mt-3 p-2 bg-green-50 rounded border-l-2 border-green-400">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Gift className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">
                    {t('subscription.newUserGift.title')}
                  </span>
                </div>
                <span className="text-sm font-semibold text-green-700">
                  {t('subscription.newUserGift.flowerScissors.included')}
                </span>
              </div>
              
              <div className="space-y-2">
                {/* New User Scissors Gift */}
                {hasScissorsGift && (
                  <div className="flex items-center">
                    {scissorsImage && (
                      <img src={scissorsImage} alt="Flower Scissors" className="w-4 h-4 mr-2 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className="text-sm font-medium text-green-800">
                        {t('subscription.newUserGift.flowerScissors.title')}
                      </span>
                      <p className="text-xs text-green-600">
                        {t('subscription.newUserGift.flowerScissors.value')}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Yearly Subscription Vase Gift */}
                {hasVaseGift && (
                  <div className="flex items-center">
                    <img src={vaseImage} alt="Premium Vase" className="w-4 h-4 mr-2 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-green-800">
                        {t('subscription.paymentMethods.freePremiumVase')}
                      </span>
                      <p className="text-xs text-green-600">
                        {t('subscription.paymentMethods.premiumVaseInfo')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        
        {/* Show next billing price for subscription payments with one-time promotions */}
        {paymentType === 'subscription' && promotionDetails?.isValid && shouldApplyDiscount && (promotionDetails?.percentOff > 0 || promotionDetails?.amountOff > 0) && (
          <div className="mt-3 p-2 bg-amber-50 rounded border-l-2 border-amber-400">
            <div className="flex items-center mb-1">
              <span className="text-xs">ℹ️</span>
              <span className="ml-1 text-xs font-medium text-amber-700">{t('subscription.plansSection.nextBillingInfo', 'Next billing information')}</span>
            </div>
            {subscriptionType === 'monthly' ? (
              <div className="text-xs text-amber-700">
                {t('subscription.plansSection.nextMonthlyBilling', 'Next month')}: {currencySymbol}{formatPrice(basePrice * 0.95, currency)}
              </div>
            ) : subscriptionType === 'yearly' ? (
              <div className="text-xs text-amber-700">
                {t('subscription.plansSection.nextYearlyBilling', 'Next year')}: {currencySymbol}{formatPrice(basePrice * 12 * 0.85, currency)}
              </div>
            ) : null}
            <div className="text-xs text-amber-600 mt-1">
              {t('subscription.plansSection.promotionFirstTimeOnly', 'Promotion applies to first payment only')}
            </div>
          </div>
        )}
        
        {/* Informational text about what is included */}
        <div className="text-xs text-gray-500 mt-2">
          <p>
            {subscriptionType === 'trial'
              ? t('subscription.orderSummary.oneTrialDelivery', 'One delivery only - try our service')
              : (subscriptionType === 'monthly'
                ? t('subscription.orderSummary.fourWeeksDeliveries')
                : t('subscription.orderSummary.fiftyTwoWeeksDeliveries'))}{' '}
            {subscriptionType !== 'trial' && (
              subscriptionLevel === 'premium'
                ? t('subscription.orderSummary.withPremiumFlowersIncluded')
                : t('subscription.orderSummary.withStandardFlowersIncluded')
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderSummarySection; 